import axios from "axios";
import querystring from "querystring";

// 从URL参数获取值，不转小写（用于token和jwt）
const getQueryParam = (name) => {
  try {
    // 优先使用 URLSearchParams（更可靠）
    const urlParams = new URLSearchParams(window.location.search);
    const value = urlParams.get(name);
    if (value) {
      return value;
    }
    
    // 备用方案：使用正则表达式（处理URLSearchParams无法处理的长值）
    // 注意：JWT值可能很长，需要匹配到下一个&或字符串结尾
    var reg = new RegExp("(^|&)" + name + "=([^&]*)", "i");
    var r = window.location.search.substr(1).match(reg);
    if (r != null && r[2]) {
      return decodeURIComponent(r[2]);
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

// 保存 token 到 sessionStorage
const saveTokenToStorage = (token) => {
  try {
    if (token) {
      sessionStorage.setItem("auth_token", token);
    } else {
      sessionStorage.removeItem("auth_token");
    }
  } catch (error) {
    console.error("保存 token 到缓存失败:", error);
  }
};

// 保存 jwt 到 sessionStorage
const saveJwtToStorage = (jwt) => {
  try {
    if (jwt) {
      sessionStorage.setItem("auth_jwt", jwt);
    } else {
      sessionStorage.removeItem("auth_jwt");
    }
  } catch (error) {
    console.error("保存 jwt 到缓存失败:", error);
  }
};

// 从缓存获取 token
const getTokenFromStorage = () => {
  try {
    return sessionStorage.getItem("auth_token");
  } catch (error) {
    return null;
  }
};

// 从缓存获取 jwt
const getJwtFromStorage = () => {
  try {
    return sessionStorage.getItem("auth_jwt");
  } catch (error) {
    return null;
  }
};

// 获取token的函数：优先从缓存读取，如果没有则从URL读取并保存到缓存
const getToken = () => {
  // 优先从缓存读取
  let token = getTokenFromStorage();
  
  // 如果缓存中没有，尝试从URL读取
  const urlToken = getQueryParam("token");
  if (urlToken) {
    // 如果 URL 中有 token，更新缓存（确保使用最新的）
    if (urlToken !== token) {
      saveTokenToStorage(urlToken);
    }
    return urlToken;
  }
  
  // 如果 URL 中也没有，返回缓存中的值（如果有）
  return token;
};

// 获取jwt的函数：优先从缓存读取，如果没有则从URL读取并保存到缓存
const getJwt = () => {
  // 优先从缓存读取
  let jwt = getJwtFromStorage();
  
  // 如果缓存中没有，尝试从URL读取
  const urlJwt = getQueryParam("jwt");
  if (urlJwt) {
    // 如果 URL 中有 jwt，更新缓存（确保使用最新的）
    if (urlJwt !== jwt) {
      saveJwtToStorage(urlJwt);
    }
    return urlJwt;
  }
  
  // 如果 URL 中也没有，返回缓存中的值（如果有）
  return jwt;
};

// 初始化：从URL读取 token 和 jwt 并保存到缓存（如果存在）
// 每次调用都会刷新缓存中的 token，确保使用最新的 token
const initAuthFromUrl = () => {
  const urlToken = getQueryParam("token");
  const urlJwt = getQueryParam("jwt");
  
  // 如果 URL 中有 token，更新缓存（即使缓存中已有，也要更新，确保使用最新的）
  if (urlToken) {
    saveTokenToStorage(urlToken);
  }
  // 如果 URL 中有 jwt，更新缓存（jwt 优先级更高）
  if (urlJwt) {
    saveJwtToStorage(urlJwt);
    // 如果有 jwt，通常不需要 token，但为了兼容性，保留 token
  }
};

// 页面加载时自动初始化
if (typeof window !== "undefined") {
  initAuthFromUrl();
}

// 导出刷新 token 的函数，供其他模块调用（如首页）
export const refreshAuthFromUrl = () => {
  initAuthFromUrl();
};

// 导出获取认证类型的方法，供其他模块判断当前使用的认证方式
export const getAuthType = () => {
  const jwt = getJwt();
  const token = getToken();
  
  if (jwt) {
    return 'jwt';
  } else if (token) {
    return 'token';
  }
  return null;
};

// 导出获取 token 和 jwt 的函数，供其他模块使用
export { getToken, getJwt };

const Http = axios.create({
  baseURL: "", //api host：api的请求基础地质
  timeout: 1000 * 60 * 10, // 超时时间ms
  withCredentials: false,
  // headers: {
  //   "Content-Type": "application/json; charset=utf-8", // 默认设置为JSON
  // },
});

// 添加请求拦截器，自动在请求头中添加token和jwt
Http.interceptors.request.use(
  (config) => {
    const token = getToken();
    const jwt = getJwt();
    
    // 确保 headers 对象存在
    config.headers = config.headers || {};
    
    // jwt优先，如果有jwt就用jwt
    if (jwt) {
      config.headers["Authorization"] = `Bearer ${jwt}`;
    } else if (token) {
      // 如果没有jwt，尝试多种常见的token格式，确保兼容性
      config.headers["token"] = token;
      config.headers["Token"] = token; // 首字母大写
      config.headers["Authorization"] = `Bearer ${token}`; // Bearer格式
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 添加响应拦截器，处理401错误
Http.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      const jwt = getJwt();
      const token = getToken();
      
      // 检查JWT是否过期
      if (jwt) {
        try {
          const parts = jwt.split(".");
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            const exp = payload.exp;
            if (exp) {
              const now = Math.floor(Date.now() / 1000);
              if (now > exp) {
                console.error("[HTTP] JWT已过期", {
                  expiredAt: new Date(exp * 1000).toLocaleString(),
                  currentTime: new Date().toLocaleString(),
                });
              } else {
                console.error("[HTTP] JWT未过期，但返回401", {
                  expiresAt: new Date(exp * 1000).toLocaleString(),
                  currentTime: new Date().toLocaleString(),
                });
              }
            }
          }
        } catch (e) {
          console.error("[HTTP] 解析JWT失败", e);
        }
      }
      
      console.error("[HTTP] 401错误详情", {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
        responseData: error.response?.data,
        hasJwt: !!jwt,
        hasToken: !!token,
      });
    }
    return Promise.reject(error);
  }
);
function createHttp(type) {
  return function ({ url, params, is_qs }) {
    let data = params || {};
    let headers = {};

    if (is_qs) {
      data = querystring.stringify(data);
      headers["Content-Type"] = "application/x-www-form-urlencoded";
    } else {
      headers["Content-Type"] = "application/json";
    }

    // 注意：token 和 jwt 会通过拦截器自动添加到 headers 中
    // 这里不需要手动添加，axios 会自动合并 headers

    return new Promise((resolve, reject) => {
      Http({
        url,
        data,
        method: type,
        headers,
      })
        .then((res) => {
          resolve(res);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };
}
export const _post = createHttp("post");
class Request {
  baseURL = "";
  constructor(baseURL) {
    this.baseURL = baseURL;
  }
  async post(url, params) {
    let config = {
      url: `${this.baseURL ? this.baseURL : ""}${url}`,
      params,
      is_qs: true,
    };
    const data = await _post(config).catch((err) => {
      return err;
    });
    if (data.status == 200) {
      return data.data;
    } else {
      return {
        status: data?.response?.status,
        ...data.response.data,
      };
    }
  }

  async postJson(url, params) {
    let config = {
      url: `${this.baseURL ? this.baseURL : ""}${url}`,
      params,
      is_qs: false, // 设置为 false 以发送 JSON 数据
    };

    const data = await _post(config).catch((err) => {
      return err;
    });
    if (data.status == 200) {
      return data.data;
    } else {
      return {
        status: data?.response?.status,
        ...data.response.data,
      };
    }
  }
}

export const $post = async (url, params, useJson = false) => {
  if (useJson) {
    return await new Request().postJson(url, params);
  }
  return await new Request().post(url, params);
};
export const $postApi = async (url, params) => {
  return await new Request("https://api.towords.net").post(url, params);
};
export const $postApiTest = async (url, params) => {
  return await new Request("http://192.168.110.95:8080").post(url, params);
};

export const $postWx = async (url, params) => {
  return await new Request("https://wx.towords.net").post(url, params);
};

export const $postBody = async (url, params) => {
  return await new Request().postJson(url, params);
};

export const $postJson = async (url, params) => {
  return await new Request().postJson(url, params);
};

export const $getApi = async (url, params) => {
  const token = getToken();
  const jwt = getJwt();
  const headers = {};
  
  if (token) {
    // 尝试多种常见的token格式
    headers["token"] = token;
    headers["Token"] = token;
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  if (jwt) {
    headers["Authorization"] = `Bearer ${jwt}`;
  }
  
  const data = await axios({
    method: "get",
    url: `https://api.towords.net${url}`,
    params: params,
    headers: headers,
  });
  if (data.status == 200) {
    return data.data;
  } else {
    return {
      status: data.response.status,
      data: data.response.data,
    };
  }
};
